"use client";

import { c as _c } from "react/compiler-runtime";
import { CreditCard, DollarSign, HelpCircle, House, Info, LogOut, Menu, MessageCircle, Settings, Truck } from "lucide-react";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "~/components/ui/dropdown-menu";
import { registerSuperProperties } from "~/lib/analytics";
import { clearAttributionCode } from "~/lib/attribution";
import { authClient } from "~/lib/auth/client";
import { cn } from "~/lib/utils";
import { useGuardedNavigate } from "~/store/navGuardStore";
import { useDriverEarningsEntry } from "./useNavAuth";
type UserMenuProps = {
  firstName: string;
  lastName: string;
  email: string;
  isHost: boolean;
  driverFeaturesEnabled: boolean;
  /** Total unread across the inbox. 0 renders no badge. */
  unreadMessages?: number;
  extraItems?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
  }[];
};
export function UserMenu(t0) {
  const $ = _c(88);
  const {
    firstName,
    lastName,
    email,
    isHost,
    driverFeaturesEnabled,
    unreadMessages: t1,
    extraItems
  } = t0;
  const unreadMessages = t1 === undefined ? 0 : t1;
  const [open, setOpen] = useState(false);
  const navigate = useGuardedNavigate();
  const pathname = usePathname();
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const mq = window.matchMedia("(min-width: 768px)");
      const handler = e => {
        if (!e.matches) {
          setOpen(false);
        }
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== pathname) {
    t4 = pathname.startsWith("/search");
    $[2] = pathname;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isSearchPage = t4;
  const showEarnings = useDriverEarningsEntry(isHost);
  pathname.startsWith("/host") ? "host" : "driver";
  const handleSignOut = _temp;
  const t5 = `${firstName[0] ?? ""}${lastName[0] ?? ""}`;
  let t6;
  if ($[4] !== t5) {
    t6 = t5.toUpperCase();
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const initials = t6;
  const t7 = open && "border-[var(--blue)] shadow-[var(--shadow-sm)]";
  let t8;
  if ($[6] !== t7) {
    t8 = cn("relative flex cursor-pointer items-center gap-2 rounded-[var(--r-full)]", "border border-[var(--ink-20)] bg-white px-3.5 py-1.5", "font-ui text-[13px] font-semibold text-[var(--ink)]", "[transition:border-color_var(--dur-fast)_var(--ease-snap),box-shadow_var(--dur-fast)_var(--ease-snap)]", "hover:border-[var(--ink-40)] hover:shadow-[var(--shadow-sm)]", "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]/30", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== firstName) {
    t9 = <span>{firstName}</span>;
    $[8] = firstName;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Menu className="h-3.5 w-3.5 text-[var(--ink-60)]" strokeWidth={2} />;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== unreadMessages) {
    t11 = unreadMessages > 0 && <span aria-hidden={true} className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-[var(--blue)] ring-2 ring-white" />;
    $[11] = unreadMessages;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== unreadMessages) {
    t12 = unreadMessages > 0 && <span className="sr-only">{" "}— {unreadMessages} unread message{unreadMessages === 1 ? "" : "s"}</span>;
    $[13] = unreadMessages;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t11 || $[16] !== t12 || $[17] !== t8 || $[18] !== t9) {
    t13 = <DropdownMenuTrigger asChild={true}><button type="button" className={t8}>{t9}{t10}{t11}{t12}</button></DropdownMenuTrigger>;
    $[15] = t11;
    $[16] = t12;
    $[17] = t8;
    $[18] = t9;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== initials) {
    t14 = <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--blue)] font-ui text-[13px] font-bold text-white">{initials}</span>;
    $[20] = initials;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== firstName || $[23] !== lastName) {
    t15 = <p className="truncate font-ui text-[13px] font-semibold text-[var(--ink)]">{firstName} {lastName}</p>;
    $[22] = firstName;
    $[23] = lastName;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== email) {
    t16 = <p className="truncate font-ui text-[11px] text-[var(--ink-60)]">{email}</p>;
    $[25] = email;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t15 || $[28] !== t16) {
    t17 = <div className="min-w-0">{t15}{t16}</div>;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t14 || $[31] !== t17) {
    t18 = <DropdownMenuLabel className="px-3 py-2.5"><div className="flex items-center gap-3">{t14}{t17}</div></DropdownMenuLabel>;
    $[30] = t14;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <DropdownMenuSeparator className="my-1 bg-[var(--ink-10)]" />;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== navigate) {
    t20 = () => navigate("/messages");
    $[34] = navigate;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <MessageCircle className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== unreadMessages) {
    t22 = unreadMessages > 0 && <span className="ml-auto grid h-4.5 min-w-4.5 place-items-center rounded-[var(--r-full)] bg-[var(--blue)] px-1.5 font-ui text-[10px] font-bold text-white">{unreadMessages > 99 ? "99+" : unreadMessages}</span>;
    $[37] = unreadMessages;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t20 || $[40] !== t22) {
    t23 = <DropdownMenuItem onSelect={t20} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]">{t21}Messages{t22}</DropdownMenuItem>;
    $[39] = t20;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== extraItems || $[43] !== navigate) {
    t24 = extraItems?.map(item => <DropdownMenuItem key={item.href} onSelect={() => navigate(item.href)} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]">{item.icon && <span className="h-4 w-4 shrink-0 text-[var(--ink-60)]">{item.icon}</span>}{item.label}</DropdownMenuItem>);
    $[42] = extraItems;
    $[43] = navigate;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== navigate || $[46] !== showEarnings) {
    t25 = showEarnings && <DropdownMenuItem onSelect={() => navigate("/account/earnings")} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]"><DollarSign className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />Earnings</DropdownMenuItem>;
    $[45] = navigate;
    $[46] = showEarnings;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== driverFeaturesEnabled || $[49] !== navigate) {
    t26 = driverFeaturesEnabled && <DropdownMenuItem onSelect={() => navigate("/account/vehicles")} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]"><Truck className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />Vehicles</DropdownMenuItem>;
    $[48] = driverFeaturesEnabled;
    $[49] = navigate;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== driverFeaturesEnabled || $[52] !== navigate) {
    t27 = driverFeaturesEnabled && <DropdownMenuItem onSelect={() => navigate("/account/payments")} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]"><CreditCard className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />Payment Methods</DropdownMenuItem>;
    $[51] = driverFeaturesEnabled;
    $[52] = navigate;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== navigate) {
    t28 = () => navigate("/account/settings");
    $[54] = navigate;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Settings className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28) {
    t30 = <DropdownMenuItem onSelect={t28} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]">{t29}Account Settings</DropdownMenuItem>;
    $[57] = t28;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== navigate) {
    t31 = () => navigate("/support");
    $[59] = navigate;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <HelpCircle className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t31) {
    t33 = <DropdownMenuItem onSelect={t31} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]">{t32}Support</DropdownMenuItem>;
    $[62] = t31;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== isSearchPage || $[65] !== navigate) {
    t34 = isSearchPage && <DropdownMenuItem onSelect={() => navigate("/about")} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]"><Info className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />About Us</DropdownMenuItem>;
    $[64] = isSearchPage;
    $[65] = navigate;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <DropdownMenuSeparator className="my-1 bg-[var(--ink-10)]" />;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== isHost || $[69] !== isSearchPage || $[70] !== navigate) {
    t36 = isSearchPage && isHost && <DropdownMenuItem onSelect={() => navigate("/host/listings")} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--ink)] focus:bg-[var(--ink-05)]"><House className="h-4 w-4 shrink-0 text-[var(--ink-60)]" />Switch to Host</DropdownMenuItem>;
    $[68] = isHost;
    $[69] = isSearchPage;
    $[70] = navigate;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <DropdownMenuItem onSelect={handleSignOut} className="gap-2.5 rounded-[var(--r-md)] px-3 py-2 font-ui text-[13px] text-[var(--red)] focus:bg-[var(--red-bg)]"><LogOut className="h-4 w-4 shrink-0 text-[var(--red)]" />Sign out</DropdownMenuItem>;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t18 || $[74] !== t23 || $[75] !== t24 || $[76] !== t25 || $[77] !== t26 || $[78] !== t27 || $[79] !== t30 || $[80] !== t33 || $[81] !== t34 || $[82] !== t36) {
    t38 = <DropdownMenuContent align="end" className="w-56 rounded-[var(--r-lg)] border border-[var(--ink-10)] bg-white p-1.5 shadow-[var(--shadow-md)]">{t18}{t19}{t23}{t24}{t25}{t26}{t27}{t30}{t33}{t34}{t35}{t36}{t37}</DropdownMenuContent>;
    $[73] = t18;
    $[74] = t23;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
    $[79] = t30;
    $[80] = t33;
    $[81] = t34;
    $[82] = t36;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== open || $[85] !== t13 || $[86] !== t38) {
    t39 = <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>{t13}{t38}</DropdownMenu>;
    $[84] = open;
    $[85] = t13;
    $[86] = t38;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  return t39;
}
async function _temp() {
  await authClient.signOut();
  try {
    posthog.reset();
    registerSuperProperties(posthog);
  } catch {}
  clearAttributionCode();
  window.location.href = "/auth";
}